// Auth context — owns the session lifecycle.
//
// Token model (00 §4.5): access_token in JS memory only (lib/api tokenStore);
// refresh_token in an httpOnly cookie the backend sets for c-ls-home-web. On boot we
// attempt ONE silent cookie-refresh so a page reload survives without re-login.
// A silent refresh yields a token but no user object (refresh returns
// {access_token, expires_in} only) — `user` may therefore be null while
// authenticated; hydrate via signaling get_self (the `self` event) or a future
// /v3/me endpoint.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { hasSessionHint, login as apiLogin, logout as apiLogout, onUnauthorized, refreshAccessToken, tokenStore } from '../lib/api';
import type { LoginUser } from '../lib/api';
import { clearCloudEventClipCache } from '../lib/recPlayback/cloudM3u8';
import { revokedTombstones } from '../features/devices/revokedTombstones';
import { signaling } from '../lib/ws';

export type AuthStatus = 'booting' | 'authenticated' | 'anonymous';

export interface AuthContextValue {
  status: AuthStatus;
  /** May be null right after a silent refresh (see header comment). */
  user: LoginUser | null;
  capabilities: Record<string, unknown> | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Merge user fields hydrated elsewhere (e.g. signaling get_self). */
  setUser: (user: LoginUser | null) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('booting');
  const [user, setUser] = useState<LoginUser | null>(null);
  const [capabilities, setCapabilities] = useState<Record<string, unknown> | null>(null);
  const booted = useRef(false);

  const endSession = useCallback(() => {
    signaling.close();
    tokenStore.clear();
    // 撤銷 tombstone 屬前一個 session:不清會串染下一個帳號的同 id 裝置(APP#123)。
    revokedTombstones.clearAll();
    void clearCloudEventClipCache();
    setUser(null);
    setCapabilities(null);
    setStatus('anonymous');
  }, []);

  // Boot: one silent refresh off the httpOnly cookie — but only when the
  // session-hint cookie says a session may exist (an anonymous boot would
  // otherwise fire a guaranteed-400 refresh and pollute the console).
  useEffect(() => {
    if (booted.current) return;
    booted.current = true;
    if (!hasSessionHint()) {
      setStatus('anonymous');
      return;
    }
    refreshAccessToken().then(
      () => setStatus('authenticated'),
      () => setStatus('anonymous')
    );
  }, []);

  // Revocation / session-expired hooks. CRITICAL (specs 18 §3.3 / 20 §3.2): only
  // ACCOUNT-scoped force_logout ends the session. Device-scoped revocations
  // (authz_revoked / device_disabled) are handled by the device list (useDeviceList)
  // — they must NOT touch the auth session — so they are deliberately NOT wired here.
  useEffect(() => {
    onUnauthorized(endSession);
    // account_suspended / account_deleted / logout / unknown → end session.
    const offForce = signaling.on('force_logout', () => endSession());
    const offExpired = signaling.on('session_expired', endSession);
    // password_changed = own rev:user from change-password (spec 20 §3.2 steps 2–5):
    // the kept session must SILENT-refresh + let the socket reconnect, NOT log out.
    // On web the refresh cookie is Path=/v3/auth/refresh-scoped, so change-password
    // usually revokes the current session too (current_session_kept=false, see
    // ChangePasswordCard) → refresh 401 → we fall through to endSession. When the
    // session IS kept, refresh succeeds and the existing reconnect backoff (which
    // refreshes the token before each attempt) brings the socket back with no logout UI.
    const offSessionRevoked = signaling.on('session_revoked', () => {
      refreshAccessToken().then(
        () => {
          // Kept session: token rotated. The socket was closed by the server; the
          // SignalingClient's own reconnect loop re-opens it with the fresh token.
          // No UI change — do NOT clear auth state here.
        },
        () => endSession(), // refresh failed (e.g. current session also revoked) → real logout.
      );
    });
    return () => {
      onUnauthorized(null);
      offForce();
      offExpired();
      offSessionRevoked();
    };
  }, [endSession]);

  // WEB3-F3: WSS 連線生命週期繫於 auth session，不繫於裝置頁路由（route-lazy 的
  // 修正）。依據：16 §2.2 L100——Web v1 無 push，WSS room 廣播是未讀 badge 唯一
  // 來源，「不在 Events tab 時收到即時事件 +1」⇒ 連線必須 tab 無關；20 §3.2——
  // change-password 須攔截本機 force_logout、admin 處置踢線亦同，socket 不在線
  // 即無從攔截。connect() 冪等（OPEN/CONNECTING no-op），且 authenticated 轉換
  // 時 tokenStore 必有 token（login/refresh 成功才轉態），open() 的無 token →
  // session_expired 路徑不會誤觸。登出/帳號級踢線走既有 endSession →
  // signaling.close()（closedByUser，不重連）。
  useEffect(() => {
    if (status === 'authenticated') signaling.connect();
  }, [status]);

  const login = useCallback(async (email: string, password: string) => {
    const data = await apiLogin(email, password);
    setUser(data.user ?? null);
    setCapabilities(data.client_capabilities ?? null);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    endSession();
  }, [endSession]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, capabilities, login, logout, setUser }),
    [status, user, capabilities, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// useOptionalAuth:provider 外回 null(純呈現層偏好如房間分組用——
// 測試環境不必為了 localStorage key 包整個 AuthProvider)。
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
